import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

interface MediaPlayerProps {
  media: {
    type: "video" | "image" | "pdf" | "website" | "shoppable";
    url: string;
    title?: string;
    description?: string;
    thumbnail?: string;
  };
  isMuted: boolean;
  videoRef?: React.RefObject<HTMLVideoElement> | null;
  autoPlay?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({
  media,
  isMuted,
  videoRef,
  onPlay,
  onPause,
  autoPlay = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const activeVideoRef = videoRef ?? localVideoRef;

  useEffect(() => {
    if (activeVideoRef.current) {
      activeVideoRef.current.muted = isMuted;
    }
  }, [isMuted, activeVideoRef]);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeVideoRef.current) {
      if (isPlaying) {
        activeVideoRef.current.pause();
        onPause?.();
      } else {
        void activeVideoRef.current.play();
        onPlay?.();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const renderMedia = () => {
    switch (media.type) {
      case "video":
        return (
          <div
            className="group relative cursor-pointer"
            onClick={togglePlayPause}
          >
            <video
              ref={activeVideoRef}
              style={{
                minWidth: "250px",
              }}
              className="max-h-[80vh] rounded-lg object-contain shadow-xl"
              src={media.url}
              autoPlay={autoPlay}
              playsInline
              controls={false}
              onPlay={() => {
                setIsPlaying(true);
                onPlay?.();
              }}
              onPause={() => {
                setIsPlaying(false);
                onPause?.();
              }}
            />
            <div
              className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-200 ${
                isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"
              }`}
            >
              <button
                className="rounded-full bg-black/50 p-4 text-white transition-colors hover:bg-black/70"
                onClick={togglePlayPause}
              >
                {isPlaying ? <Pause size={32} /> : <Play size={32} />}
              </button>
            </div>
          </div>
        );
      case "image":
        return (
          <img
            src={media.url}
            alt="Content"
            style={{
              minWidth: "290px",
            }}
            className="max-h-[80vh] w-full rounded-lg object-contain shadow-xl"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative mx-auto flex w-full items-center justify-center">
      {renderMedia()}
    </div>
  );
};

export default MediaPlayer;
